import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import Layout from '../layouts/user';
import useProductSchema from './hooks/useProductSchema';
import { SchemaLoadingScreen, ProductNotFoundScreen } from './ProductPageStates';
import GenericProductListing from './GenericProductListing';
import GenericTradeEntry from './GenericTradeEntry';
import './productEngine.css';

/**
 * ProductListingPage: Route component for /portal/invest/trade/:productType.
 *
 * Renders the variant/currency-pair catalog when the product has 2+ cards.
 * Products without a real catalog skip it explicitly:
 *   - exactly 1 card  → redirect (replace) to that card's trade-entry route,
 *     so the user never sees a pointless one-card catalog
 *   - 0 cards + form fields → the trade-entry form renders here directly
 *     (there is no /:source/:target URL to redirect to)
 *   - neither cards nor fields → Product Not Found
 */
const ProductListingPage = () => {
  const { productType } = useParams();
  const { schema, isLoading } = useProductSchema(productType);

  if (isLoading) {
    return <SchemaLoadingScreen />;
  }

  if (!schema) {
    return <ProductNotFoundScreen productType={productType} />;
  }

  const cards = Array.isArray(schema.cards) ? schema.cards : [];
  const hasFields =
    (Array.isArray(schema.fields) && schema.fields.length > 0) ||
    (schema.fields &&
      typeof schema.fields === 'object' &&
      Object.keys(schema.fields).length > 0);

  if (cards.length === 0) {
    if (!hasFields) {
      return <ProductNotFoundScreen productType={productType} />;
    }
    return (
      <Layout menu="plans">
        <div className="product-engine-ui">
          <GenericTradeEntry schema={schema} />
        </div>
      </Layout>
    );
  }

  const [onlyCard] = cards;
  if (cards.length === 1 && onlyCard?.source && onlyCard?.target) {
    return (
      <Navigate
        replace
        to={`/portal/invest/trade/${productType}/${onlyCard.source}/${onlyCard.target}`}
      />
    );
  }

  return (
    <Layout menu="plans">
      <div className="product-engine-ui">
        <GenericProductListing schema={schema} />
      </div>
    </Layout>
  );
};

export default ProductListingPage;
